<template>
	<span>
		<span v-if="lastApplication" class="application-cell-container">
			<span class="application-name">{{ lastApplication.job_name }}</span>

			<span v-if="lastApplication.match_score !== null && lastApplication.match_score !== undefined" class="score-container">
				<span class="score-badge" :class="getScoreClass(lastApplication.match_score)">
					{{ lastApplication.match_score }}
				</span>
				<span class="score-bar">
					<span
						class="score-fill"
						:style="{ width: lastApplication.match_score + '%' }"
						:class="getScoreClass(lastApplication.match_score)"
					></span>
				</span>
			</span>
		</span>
		<span v-if="applications.length > 1">
			<v-menu
				open-on-hover
				:open-delay="200"
				:close-delay="300"
				offset-x
				right
				content-class="applications-tooltip-content"
			>
				<template v-slot:activator="{ on }">
					<v-btn x-small color="primary" text v-on="on">
						{{
							$t('text.x-more', {
								count: applications.length - 1,
							})
						}}
					</v-btn>
				</template>
				<div class="applications-tooltip">
					<div class="tooltip-header">
						{{ $t('subheader.applications') }} ({{ applications.length }})
					</div>
					<div class="applications-tooltip-body">
					<div
						v-for="(application, index) in applications"
						:key="`application_${index}`"
						class="tooltip-app-row"
						:class="{ 'tooltip-app-latest': index === 0 }"
					>
						<div class="tooltip-app-job">
							<span class="tooltip-job-name" :title="application.job_name">{{ application.job_name }}</span>
							<span
								v-if="application.match_score !== null && application.match_score !== undefined"
								class="tooltip-score-badge"
								:class="getScoreClass(application.match_score)"
							>
								{{ application.match_score }}
							</span>
							<span v-if="index === 0" class="tooltip-latest-tag">
								{{ $t('label.latest') }}
							</span>
						</div>
						<span class="tooltip-app-date">
							{{ formatDate({ timestamp: application.created }) }}
						</span>
						<span
							class="tooltip-status-badge"
							:class="getStatusClass(application)"
						>
							{{
								$t(
									`pipeline.steps.${handleGetPipelineStatus({
										is_archived: application.is_archived,
										is_lead: application.is_lead,
										is_interview: application.is_interview,
										archive_status: application.archive_status,
										lead_status: application.lead_status,
										interview_status: application.interview_status,
									})}`,
								)
							}}
						</span>
					</div>
					</div>
				</div>
			</v-menu>
		</span>
	</span>
</template>

<script>
import { format } from 'date-fns';

export default {
	name: 'ApplicationsCell',
	props: {
		applications: {
			type: Array,
			required: false,
			default() {
				return [];
			},
		},
	},
	computed: {
		lastApplication() {
			const { applications } = this;
			return applications.length > 0 ? applications[0] : null;
		},
	},
	data() {
		return {
			format,
		};
	},
	methods: {
		formatDate({ timestamp = new Date(), format = 'dd.MM.yyyy' }) {
			const dateToFormat = new Date(timestamp);
			return this.format(dateToFormat, format);
		},
		handleGetPipelineStatus({
			is_archived,
			is_lead,
			is_interview,
			archive_status,
			lead_status,
			interview_status,
		}) {
			if (is_archived) {
				return archive_status ? archive_status : 'underqualified';
			} else if (is_interview) {
				return interview_status ? interview_status : 'phone';
			} else if (is_lead) {
				return lead_status ? lead_status : 'new-lead';
			}
			return 'new-applicant';
		},
		getScoreClass(score) {
			if (score >= 80) return 'score-excellent';
			if (score >= 60) return 'score-good';
			if (score >= 40) return 'score-fair';
			return 'score-poor';
		},
		getStatusClass({ is_archived, is_lead, is_interview, archive_status }) {
			if (is_archived) {
				const hiredStatuses = ['hired', 'archive-hired'];
				return hiredStatuses.includes(archive_status) ? 'status-hired' : 'status-archived';
			}
			if (is_interview) return 'status-interview';
			if (is_lead) return 'status-lead';
			return 'status-new';
		},
	},
};
</script>

<style scoped>
.application-cell-container {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.application-name {
	display: inline-block;
}

.score-container {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin-left: 4px;
}

.score-badge {
	font-weight: 600;
	padding: 2px 6px;
	border-radius: 4px;
	font-size: 11px;
	min-width: 28px;
	text-align: center;
	display: inline-block;
}

.score-bar {
	display: inline-block;
	width: 50px;
	height: 4px;
	background: #e9ecef;
	border-radius: 2px;
	overflow: hidden;
	vertical-align: middle;
}

.score-fill {
	display: block;
	height: 100%;
	transition: width 0.3s ease;
}

/* Score color classes - Light theme */
.score-excellent { background: #d4edda; color: #155724; }
.score-good { background: #d1ecf1; color: #0c5460; }
.score-fair { background: #fff3cd; color: #856404; }
.score-poor { background: #f8d7da; color: #721c24; }

/* Dark theme support */
.theme--dark .score-bar {
	background: rgba(255, 255, 255, 0.1);
}
.theme--dark .score-excellent { background: rgba(76, 175, 80, 0.3); color: #a5d6a7; }
.theme--dark .score-good { background: rgba(33, 150, 243, 0.3); color: #90caf9; }
.theme--dark .score-fair { background: rgba(255, 193, 7, 0.3); color: #ffe082; }
.theme--dark .score-poor { background: rgba(244, 67, 54, 0.3); color: #ef9a9a; }

/* ===== Tooltip Design A: Clean Card ===== */
.applications-tooltip {
	min-width: 420px;
	max-width: 560px;
}

.applications-tooltip-body {
	max-height: 280px;
	overflow-y: auto;
}

.tooltip-header {
	padding: 8px 12px;
	border-bottom: 1px solid #eee;
	font-size: 11px;
	font-weight: 600;
	color: #888;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.tooltip-app-row {
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto auto;
	align-items: center;
	padding: 8px 12px;
	gap: 14px;
	border-bottom: 1px solid #f5f5f5;
}
.tooltip-app-row:last-child {
	border-bottom: none;
}

.tooltip-app-latest {
	background: #f0f7ff;
}

.tooltip-app-job {
	display: flex;
	align-items: center;
	gap: 6px;
	min-width: 0;
}

.tooltip-job-name {
	font-size: 13px;
	font-weight: 500;
	color: #333;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.tooltip-score-badge {
	font-weight: 600;
	padding: 1px 5px;
	border-radius: 3px;
	font-size: 10px;
	min-width: 24px;
	text-align: center;
	display: inline-block;
	flex-shrink: 0;
}

.tooltip-latest-tag {
	font-size: 9px;
	padding: 1px 5px;
	border-radius: 3px;
	background: #e3f2fd;
	color: #1565c0;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.3px;
	flex-shrink: 0;
}

.tooltip-app-date {
	font-size: 12px;
	color: #888;
	white-space: nowrap;
}

.tooltip-status-badge {
	font-size: 11px;
	padding: 3px 8px;
	border-radius: 4px;
	font-weight: 500;
	white-space: nowrap;
}

/* Status colors - Light theme */
.status-new { background: #e8f5e9; color: #2e7d32; }
.status-interview { background: #fff3e0; color: #e65100; }
.status-lead { background: #e3f2fd; color: #1565c0; }
.status-hired { background: #e8f5e9; color: #1b5e20; }
.status-archived { background: #f5f5f5; color: #757575; }
.status-future { background: #f3e5f5; color: #6a1b9a; }
.status-default { background: #f5f5f5; color: #616161; }

/* Dark theme - tooltip */
.theme--dark .tooltip-header {
	border-bottom-color: rgba(255,255,255,0.08);
	color: #aaa;
}
.theme--dark .tooltip-app-row {
	border-bottom-color: rgba(255,255,255,0.05);
}
.theme--dark .tooltip-app-latest {
	background: rgba(25, 118, 210, 0.08);
}
.theme--dark .tooltip-job-name {
	color: #e0e0e0;
}
.theme--dark .tooltip-app-date {
	color: #888;
}
.theme--dark .tooltip-latest-tag {
	background: rgba(25, 118, 210, 0.2);
	color: #64b5f6;
}

/* Dark theme status colors */
.theme--dark .status-new { background: rgba(76,175,80,0.15); color: #81c784; }
.theme--dark .status-interview { background: rgba(255,152,0,0.15); color: #ffb74d; }
.theme--dark .status-lead { background: rgba(33,150,243,0.15); color: #64b5f6; }
.theme--dark .status-hired { background: rgba(76,175,80,0.2); color: #a5d6a7; }
.theme--dark .status-archived { background: rgba(255,255,255,0.06); color: #999; }
.theme--dark .status-future { background: rgba(156,39,176,0.15); color: #ce93d8; }
.theme--dark .status-default { background: rgba(255,255,255,0.06); color: #999; }
</style>

<style>
/* Unscoped: override Vuetify menu container (rendered outside component tree) */
.applications-tooltip-content.v-menu__content {
	background: #ffffff !important;
	border-radius: 8px !important;
	box-shadow: 0 4px 20px rgba(0,0,0,0.15), 0 1px 4px rgba(0,0,0,0.1) !important;
	padding: 0 !important;
	overflow: hidden;
}

.theme--dark .applications-tooltip-content.v-menu__content {
	background: #1e1e1e !important;
	box-shadow: 0 4px 20px rgba(0,0,0,0.4), 0 1px 4px rgba(0,0,0,0.3) !important;
}
</style>
